/**
 * Shared chrome for Privacy / Terms pages.
 */
import type { ReactNode } from "react";
import { Link } from "wouter";
import { ChevronLeft } from "lucide-react";
import { BRAND } from "@shared/brand";
import { SiteFooter } from "@/components/site-footer";

export function LegalLayout({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #0a0a18 0%, #05050f 40%, #05050f 100%)",
        color: "rgba(255,248,231,0.92)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "14px max(16px, env(safe-area-inset-left))",
          paddingTop: "max(14px, env(safe-area-inset-top))",
          borderBottom: "1px solid rgba(240,193,75,0.18)",
          background: "rgba(0,0,0,0.35)",
          backdropFilter: "blur(10px)",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Link
          href="/"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "rgba(255,255,255,0.65)",
            textDecoration: "none",
            fontSize: 14,
          }}
        >
          <ChevronLeft size={18} /> Home
        </Link>
        <div
          style={{
            fontFamily: "'Bebas Neue', Impact, sans-serif",
            letterSpacing: "0.12em",
            color: "#f0c14b",
            fontSize: 18,
          }}
        >
          {BRAND.shortName}
        </div>
        <Link
          href="/lobby"
          style={{ color: "rgba(255,255,255,0.65)", textDecoration: "none", fontSize: 14 }}
        >
          Lobby
        </Link>
      </header>

      <main
        style={{
          flex: 1,
          width: "100%",
          maxWidth: 760,
          margin: "0 auto",
          padding: "28px max(16px, env(safe-area-inset-left)) 40px",
        }}
      >
        <p
          style={{
            margin: "0 0 8px",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(240,193,75,0.75)",
            fontWeight: 700,
          }}
        >
          Legal
        </p>
        <h1
          style={{
            margin: "0 0 8px",
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "clamp(1.75rem, 5vw, 2.35rem)",
            fontWeight: 700,
            color: "#fff8e7",
          }}
        >
          {title}
        </h1>
        <p style={{ margin: "0 0 28px", fontSize: 13, color: "rgba(255,255,255,0.45)" }}>
          {BRAND.name} · Last updated {updated}
        </p>

        <article
          className="legal-prose"
          style={{
            lineHeight: 1.65,
            fontSize: 15,
            color: "rgba(255,248,231,0.88)",
          }}
        >
          {children}
        </article>

        <nav
          style={{
            marginTop: 36,
            paddingTop: 20,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            fontSize: 14,
          }}
        >
          <Link href="/privacy" style={{ color: "#9cf" }}>
            Privacy Policy
          </Link>
          <Link href="/tos" style={{ color: "#9cf" }}>
            Terms of Service
          </Link>
          <Link href="/lobby" style={{ color: "#9cf" }}>
            Play lobby
          </Link>
        </nav>
      </main>

      <SiteFooter compact />

      <style>{`
        .legal-prose h2 {
          font-family: 'Cinzel', Georgia, serif;
          font-size: 1.15rem;
          margin: 1.75rem 0 0.65rem;
          color: #f0c14b;
          letter-spacing: 0.04em;
        }
        .legal-prose p { margin: 0 0 0.9rem; }
        .legal-prose ul { margin: 0 0 1rem; padding-left: 1.25rem; }
        .legal-prose li { margin-bottom: 0.4rem; }
        .legal-prose a { color: #9cf; }
        .legal-prose strong { color: #fff8e7; }
      `}</style>
    </div>
  );
}
